import { Outlet } from 'react-router-dom';
import { ReactElement } from 'react';
import { Sidebar } from '../Sidebar';
import { TopBar } from '../TopBar';
import classes from './Appshell.module.css';
import {
  RouterMetadataProvider,
  useRouterMetadataContext,
} from '../../contexts/routerMetaDataContext.tsx';
import { TeamSelectorHeader } from '../TeamSelectorHeader/TeamSelectorHeader.tsx';
import { SelectedUnitContextProvider } from '../../contexts/selectedUnitContext.tsx';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo.ts';
import { MobileMenu } from '../MobileMenu';
import { useTabletAndDownQuery } from '../../hooks/useTabletAndDownQuery.ts';
import { PageLoader } from '../PageLoader/PageLoader.tsx';
import { Alert } from '../Alert/Alert.tsx';
import { Paragraph } from '../Paragraph';
import { Breadcrumbs } from '../Breadcrumbs';
import cn from 'classnames';
import { SkipLink } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';
import { useIsLoggedInRedirect } from '../../hooks/useIsLoggedInRedirect/useIsLoggedInRedirect.ts';
import { useSaveUserLanguage } from '../../hooks/useSaveUserLanguage.ts';
import { useReturnAfterLogin } from '../../hooks/useReturnAfterLogin.ts';

export const AppShell = (): ReactElement => {
  const {isLoggedInData, isLoadingIsLoggedIn, hasIsLoggedInError} = useIsLoggedInRedirect();
  useReturnAfterLogin(isLoggedInData?.isLoggedIn ?? false);

  if (isLoadingIsLoggedIn) {
    return <PageLoader />;
  }

  if (hasIsLoggedInError) {
    return (
      <div>
        <Alert title={hasIsLoggedInError.name} variant="danger" size="lg">
          <Paragraph size="md">{hasIsLoggedInError.message}</Paragraph>
        </Alert>
      </div>
    );
  }

  if (isLoggedInData?.isLoggedIn) {
    return (
      <RouterMetadataProvider>
        <SelectedUnitContextProvider>
          <AppShellTemplate />
        </SelectedUnitContextProvider>
      </RouterMetadataProvider>
    );
  }

  return <PageLoader />;
};

const AppShellTemplate = () => {
  const { t } = useTranslation();
  const { isLoading: isLoadingUserInfo, data: userInfo } = useGetUserinfo();
  useSaveUserLanguage();
  const { shouldShowTeamSelector, isTeamSelectorInPage, hasBreadcrumbsInPage, shouldBeInFocusMode } =
    useRouterMetadataContext();

  const isTabletAndDown = useTabletAndDownQuery();

  const containerClass = cn(classes.container, {
    [classes.mobileContainer]: isTabletAndDown,
  });

  // This is just to ensure we load necessary start-up data.
  if (isLoadingUserInfo) {
    return <PageLoader />;
  }

  const showSidebar = !shouldBeInFocusMode;

  return (
    <>
      <SkipLink href="#main">{t('sidebar.skipToContent')}</SkipLink>
      <div className={containerClass}>
        {showSidebar && (isTabletAndDown ? <MobileMenu /> : <Sidebar />)}
        <main
          id="main"
          tabIndex={-1}
          className={cn(classes.outletContainer, {
            [classes.mobileOutlet]: isTabletAndDown,
            [classes.focusModeOutlet]: shouldBeInFocusMode,
            [classes.withTopBar]: showSidebar && !isTabletAndDown,
          })}
        >
          {showSidebar && !isTabletAndDown && <TopBar />}
          {shouldShowTeamSelector && !isTeamSelectorInPage && <TeamSelectorHeader />}
          {!shouldBeInFocusMode && !hasBreadcrumbsInPage && <Breadcrumbs />}
          <Outlet />
        </main>
      </div>
    </>
  );
};
